import { useCallback, useEffect, useState } from "react"
import useSWR from "swr"
import { ClientUserState } from "types"
import { ApiUserData } from "src/models/user/ApiUserData"
import { fetchUserFollowsCount } from "src/models/user/fetchUserFollowsCount"
import TieupImg from "src/components/common/TieupImg"
import { useOwnTieups } from "src/components/hooks/useOwnTieups"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import TimeUtil from "src/utils/TimeUtil"
import styles from "./index.module.scss"
import clsx from "clsx"
import { TieupRequestStatus } from "../../../models/tieup/TieupRequestStatus"
import { useCreatorFollow } from "../../../models/follow/useCreatorFollow"

type Props = {
  user: ApiUserData
  apiUserData: ApiUserData | null
  clientState: ClientUserState
}

export const ProfileCard: React.FC<Props> = ({
  user,
  apiUserData,
  clientState,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { addToastError, addToastInfo } = useTieupToast()

  const { ownTieups } = useOwnTieups()

  const isMe = apiUserData?.id === user.id

  const hasTieup = ownTieups?.some((v) => {
    if (v.status !== TieupRequestStatus.Accepted) {
      return false
    }

    return v.userId === user.id || v.appliedUserId === user.id
  })

  const diff = TimeUtil.diffDateFromNow(user.last_login_datetime)

  const lastLoginText = diff.hours >= 24 ? `${diff.days}日前` : "24時間以内"

  const { isFollowing, followCreator, unfollowCreator } = useCreatorFollow(
    user.id,
    clientState === 2,
  )

  const { data: followsCount } = useSWR(
    `/v1/user/${user.display_name}/followings`,
    () => fetchUserFollowsCount(user.display_name),
  )

  const [followButtonText, setFollowButtonText] = useState("フォロー")

  useEffect(() => {
    setFollowButtonText(isFollowing ? "フォロー中" : "フォロー")
  }, [isFollowing])

  const handleMouseEnter = useCallback(() => {
    if (isFollowing) {
      setFollowButtonText("フォロー解除")
    }
  }, [isFollowing])

  const handleMouseLeave = useCallback(() => {
    if (isFollowing) {
      setFollowButtonText("フォロー中")
    }
  }, [isFollowing])

  const follow = async () => {
    try {
      setIsLoading(true)
      await followCreator()
      addToastInfo("フォローしました")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      switch (error.response.status) {
        case 409:
          addToastError("すでにフォローしています。")
          break
        case 412:
          addToastError("フォロー登録上限数に達しています")
          break
        default:
          addToastError("フォロー追加に失敗しました。")
          break
      }
    } finally {
      setIsLoading(false)
    }
  }

  const unfollow = async () => {
    try {
      setIsLoading(true)
      await unfollowCreator()
      addToastInfo("フォローを解除しました。")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      addToastError("フォロー解除に失敗しました。")
    } finally {
      setIsLoading(false)
    }
  }

  const handleClickToggle = () => {
    if (
      isFollowing === undefined ||
      clientState !== 2 ||
      apiUserData?.id === user.id
    )
      return

    isFollowing ? unfollow() : follow()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <div className={styles.image}>
          <TieupImg
            src={user.icon_url || "/img/user-icon.png"}
            alt={user.name}
          />
        </div>
        <div className={styles.user}>
          <h1 className={styles.user_name}>{user.name}</h1>
          <p className={styles.user_display_name}>{user.display_name}</p>
        </div>

        <div className={styles.profile}>
          {user.profile && (
            <div className={styles.profile_col}>
              <p className={styles.profile_text}>{user.profile}</p>
            </div>
          )}

          <div className={styles.profile_col}>
            <p className={styles.follow}>
              <span className={styles.follow_count}>{followsCount}</span>{" "}
              フォロー
            </p>

            {(user.twitter_url || user.external_work_url || user.pixiv_url) && (
              <ul className={styles.externalLinks}>
                {user.twitter_url && (
                  <li className={styles.externalLinks_item}>
                    <a
                      className={styles.externalLinks_icon}
                      href={user.twitter_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src="/img/icn_twitter.png" alt="Twitter" />
                    </a>
                  </li>
                )}
                {user.external_work_url && (
                  <li className={styles.externalLinks_item}>
                    <a
                      className={styles.externalLinks_icon}
                      href={user.external_work_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/img/ico_external_novel.svg"
                        alt="小説投稿サイト"
                      />
                    </a>
                  </li>
                )}
                {user.pixiv_url && (
                  <li className={styles.externalLinks_item}>
                    <a
                      className={styles.externalLinks_icon}
                      href={user.pixiv_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/img/ico_external_pic.svg"
                        alt="画像投稿サイト"
                      />
                    </a>
                  </li>
                )}
              </ul>
            )}
          </div>
          <div className={styles.profile_col}>
            <ul className={styles.profile_asideList}>
              {!isMe && hasTieup && (
                <li className={styles.hasTieup}>たいあっぷ済み</li>
              )}
              <li className={styles.lastLoginDate}>
                <time dateTime={user.last_login_datetime}>{lastLoginText}</time>
              </li>
            </ul>
          </div>
        </div>

        {apiUserData && clientState === 2 && apiUserData.id !== user.id && (
          <button
            disabled={isLoading}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClickToggle}
            className={clsx({
              [styles.followButton]: true,
              [styles.isFollow]: isFollowing,
            })}
          >
            {followButtonText}
          </button>
        )}
      </div>
    </div>
  )
}
